import { Helmet } from "react-helmet-async";

export default function RESTPilotPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <Helmet>
        <title>Privacy Policy - RESTPilot | Cosmovex</title>
        <meta
          name="description"
          content="Privacy Policy for RESTPilot, the Cosmovex mobile-first REST API testing app."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/restpilot/privacy-policy"
        />
      </Helmet>

      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - RESTPilot
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-03-08">March 8, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>RESTPilot</strong> is a mobile-first REST API testing app built by{' '}
            <strong>Cosmovex</strong>. This Privacy Policy explains what information
            RESTPilot may process, how that information is used, and the choices you
            have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Information We Process</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Requests you create:</strong> request names, URLs, headers, bodies,
              authentication tokens, environments, saved collections, and request history
              may be stored locally on your device so the app can restore your workspace.
            </li>
            <li>
              <strong>Environment values:</strong> base URLs, tokens, and custom variables
              are stored locally if you choose to save them for reuse.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob or related
              Google Mobile Ads services may collect device and advertising data as
              described in Google's own privacy documentation.
            </li>
            <li>
              <strong>Diagnostics:</strong> if analytics, crash reporting, or similar
              diagnostics are enabled in future releases, they may collect app stability
              and usage signals to improve reliability.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">How We Use Information</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>To let you create, send, save, duplicate, import, and re-run API requests.</li>
            <li>To remember your environments, quick shortcuts, theme choice, and request history.</li>
            <li>To improve app performance, reliability, onboarding, and product quality.</li>
            <li>To display ads in supported versions of the app, where applicable.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage and Sensitive Data</h2>
          <p>
            RESTPilot is designed to keep core workspace data on your device. Saved
            requests, response history, tokens, and environment values may remain stored
            locally until you edit or delete them. Because API credentials can be sensitive,
            you should avoid storing production secrets on shared or untrusted devices.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            RESTPilot may use <strong>Google AdMob</strong> to show banner or other ad
            formats. AdMob may collect and process identifiers, device information,
            approximate location, and ad interaction data to deliver and measure ads.
            Any such processing is governed in part by Google's privacy terms and the
            settings available on your device and Google account.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Third-Party Services</h2>
          <p>RESTPilot may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Shared device storage APIs provided by Android or iOS</li>
            <li>Any analytics or crash-reporting tools added in future releases</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your saved requests or workspace data. Data may be shared only
            with service providers required to operate app features, such as advertising,
            app distribution, security, or diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear the app's
            storage, or uninstall the app. Data collected by third-party services such as
            AdMob is retained according to those providers' own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Children's Privacy</h2>
          <p>
            RESTPilot is not intended for children under 13, and we do not knowingly
            collect personal information from children through the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app, but no method
            of storage or transmission is completely secure. You are responsible for the
            APIs and credentials you choose to use inside RESTPilot.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You can delete saved requests, environments, and history from inside the app.</li>
            <li>You can uninstall the app to remove locally stored data from your device.</li>
            <li>You can manage ad personalization and advertising controls through your device and Google settings.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will post
            the updated version on this page and revise the effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about RESTPilot, contact{' '}
            <a href="mailto:trionapps@gmail.com" className="text-[#0F6BBF] underline">
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
